import React, { useState } from 'react';
import {
  IconButton,
  Badge,
  Menu,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  Typography,
  Box,
  Button,
  Divider
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  CheckCircle as CheckCircleIcon,
  ArrowForward as ArrowForwardIcon,
  Assignment as AssignmentIcon,
  Flight as FlightIcon
} from '@mui/icons-material';
import { useNotifications } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';
import { ROLES, isFlightCrewRole } from '../../constants/roles';
import { useNavigate } from 'react-router-dom';

const NotificationsBell = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const { notifications, unreadCount, markAsRead, markAllAsRead, clearAll } = useNotifications();
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMarkAsRead = (notificationId) => {
    markAsRead(notificationId);
  };

  const handleMarkAllAsRead = () => {
    markAllAsRead();
    handleClose();
  };

  const handleClearAll = () => {
    clearAll();
    handleClose();
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const isRead = (notification) => {
    // Get userId from user object stored in localStorage (user.id, not userId)
    const userData = localStorage.getItem('user');
    const userId = userData ? JSON.parse(userData).id : null;
    return notification.readBy?.some(r => String(r.userId) === String(userId));
  };

  const handleNotificationClick = (notification) => {
    markAsRead(notification._id);
    
    // Navigate based on notification meta data and user role
    if (notification.meta) {
      const { entityType, entityId, tripId, charterNumber, sectorId, sectorSeqNo } = notification.meta;
      const userRole = user?.role;
      
      // Determine the appropriate route based on user role and entity type
      if (entityType === 'TRIP' || charterNumber) {
        // For TRIP/Charter notifications - navigate directly to the charter
        if ([ROLES.SALES].includes(userRole)) {
          // SALES goes to Sales Enquiry
          navigate('/sales', { state: { selectedTripId: tripId || entityId, charterNumber } });
        } else if ([ROLES.CA_MANAGER, ROLES.CA_TECHNICAL_OFFICER, ROLES.PLANNING_MANAGER, ROLES.CAMO_QM_MANAGER, ROLES.CAMO_QM_OFFICER].includes(userRole)) {
          // CAMO roles go to CAMO dashboard
          navigate('/camo-dashboard');
        } else if ([ROLES.OPERATIONS_MANAGER, ROLES.OPERATIONS_EXECUTIVE, ROLES.ACCOUNTABLE_MANAGER, ROLES.SUPER_ADMIN].includes(userRole)) {
          // Operations roles go to Operations Board
          navigate('/ops', { state: { selectedTripId: tripId || entityId, charterNumber } });
        } else {
          // Default fallback (shouldn't happen, but safe)
          navigate('/sales', { state: { selectedTripId: tripId || entityId, charterNumber } });
        }
      } else if (entityType === 'SECTOR') {
        // For SECTOR notifications - navigate directly to the sector
        if (isFlightCrewRole(userRole)) {
          // CREW goes to Crew Inbox (their assignments)
          navigate('/crew', { state: { selectedSectorId: sectorId || entityId, tripId, charterNumber } });
        } else if (userRole === ROLES.SALES) {
          // SALES goes to Sales Enquiry (can view sectors there)
          navigate('/sales', { state: { selectedTripId: tripId, selectedSectorId: sectorId || entityId, charterNumber, sectorSeqNo } });
        } else if ([ROLES.CA_MANAGER, ROLES.CA_TECHNICAL_OFFICER, ROLES.PLANNING_MANAGER, ROLES.CAMO_QM_MANAGER, ROLES.CAMO_QM_OFFICER].includes(userRole)) {
          // CAMO roles go to CAMO dashboard
          navigate('/camo-dashboard');
        } else if ([ROLES.OPERATIONS_MANAGER, ROLES.OPERATIONS_EXECUTIVE, ROLES.ACCOUNTABLE_MANAGER, ROLES.SUPER_ADMIN].includes(userRole)) {
          // Operations roles go to Operations Board
          navigate('/ops', { state: { selectedTripId: tripId, selectedSectorId: sectorId || entityId, charterNumber, sectorSeqNo } });
        } else {
          // Default fallback
          navigate('/sales', { state: { selectedTripId: tripId, selectedSectorId: sectorId || entityId, charterNumber, sectorSeqNo } });
        }
      }
    }
    
    handleClose();
  };

  const getActionLabel = (notification) => {
    if (!notification.meta) return null;
    const { entityType } = notification.meta;
    
    if (entityType === 'SECTOR') {
      return 'View Sector';
    } else if (entityType === 'TRIP') {
      return 'View Charter';
    }
    return 'View Details';
  };

  const getActionIcon = (notification) => {
    if (!notification.meta) return <ArrowForwardIcon />;
    const { entityType } = notification.meta;
    
    if (entityType === 'SECTOR') {
      return <AssignmentIcon />;
    } else if (entityType === 'TRIP') {
      return <FlightIcon />;
    }
    return <ArrowForwardIcon />;
  };

  return (
    <>
      <IconButton
        color="inherit"
        onClick={handleClick}
        aria-label="notifications"
      >
        <Badge badgeContent={unreadCount} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>
      
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          style: {
            width: 350,
            maxHeight: 400
          }
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6">Notifications</Typography>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {unreadCount > 0 && (
                <Button size="small" onClick={handleMarkAllAsRead}>
                  Mark all read
                </Button>
              )}
              {notifications.length > 0 && (
                <Button size="small" onClick={handleClearAll} color="secondary">
                  Clear All
                </Button>
              )}
            </Box>
          </Box>
          <Divider />
        </Box>

        {notifications.length === 0 ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No notifications
            </Typography>
          </Box>
        ) : (
          <List sx={{ maxHeight: 300, overflow: 'auto' }}>
            {notifications.slice(0, 100).map((notification) => (
              <ListItem
                key={notification._id}
                sx={{
                  backgroundColor: isRead(notification) ? 'transparent' : 'action.hover',
                  '&:hover': { backgroundColor: 'action.selected' },
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  py: 1.5
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: isRead(notification) ? 'normal' : 'bold' }}>
                          {notification.title}
                        </Typography>
                        {!isRead(notification) && (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMarkAsRead(notification._id);
                            }}
                            color="primary"
                          >
                            <CheckCircleIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {notification.body}
                        </Typography>
                        {notification.meta && (
                          <Box sx={{ mt: 0.5 }}>
                            {notification.meta.charterNumber && (
                              <Typography variant="caption" color="primary" sx={{ mr: 1, fontWeight: 'medium' }}>
                                Charter: {notification.meta.charterNumber}
                              </Typography>
                            )}
                            {notification.meta.sectorSeqNo && (
                              <Typography variant="caption" color="primary" sx={{ mr: 1, fontWeight: 'medium' }}>
                                Sector: {notification.meta.sectorSeqNo}
                              </Typography>
                            )}
                          </Box>
                        )}
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          {formatTime(notification.createdAt)}
                        </Typography>
                      </Box>
                    }
                  />
                </Box>
                {notification.meta && (notification.meta.entityType === 'SECTOR' || notification.meta.entityType === 'TRIP') && (
                  <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={getActionIcon(notification)}
                      onClick={() => handleNotificationClick(notification)}
                      sx={{ textTransform: 'none' }}
                    >
                      {getActionLabel(notification)}
                    </Button>
                  </Box>
                )}
              </ListItem>
            ))}
          </List>
        )}
      </Menu>
    </>
  );
};

export default NotificationsBell;
